// ABOUTME: Settings page: link behavior, bookmark import/export, and the danger zone.
// ABOUTME: Data and handlers come from the app shell so this page never opens a second subscription.

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useUIStore } from "../stores/ui";
import type { DashboardItem } from "../services/dashboardService";
import { planBookmarkImport, describeImportResult } from "../utils/bookmarkImport";
import { downloadBookmarksFile, exportableLinkCount } from "../utils/bookmarkExport";
import ConfirmDialog from "./ConfirmDialog";

interface Bookmark {
  href: string;
  title: string;
  folder: string;
  checked: boolean;
}

interface SettingsProps {
  dashboardItems: DashboardItem[];
  /** Appends planned items in one write. Rejects on failure. */
  onImport: (items: Omit<DashboardItem, "id">[]) => Promise<void>;
  /** Removes every link (widgets stay). Rejects on failure. */
  onDeleteAllLinks: () => Promise<void>;
}

const Settings: React.FC<SettingsProps> = ({ dashboardItems, onImport, onDeleteAllLinks }) => {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [parseError, setParseError] = useState<string>("");
  const [selectAll, setSelectAll] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState<string>("");
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [deleteNotice, setDeleteNotice] = useState<{ kind: "ok" | "error"; text: string } | null>(
    null
  );
  const openLinksInNewTab = useUIStore((s) => s.openLinksInNewTab);
  const setOpenLinksInNewTab = useUIStore((s) => s.setOpenLinksInNewTab);
  const columnCount = useUIStore((s) => s.columnCount);

  const linkCount = exportableLinkCount(dashboardItems);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setParseError("");
      setImportSuccess("");
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const html = evt.target?.result as string;
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");
          const links = Array.from(doc.querySelectorAll("a")).map((a) => ({
            href: a.getAttribute("href") || "",
            title: a.textContent || "",
            folder: a.closest("dl")?.previousElementSibling?.textContent || "",
            checked: true,
          }));
          setBookmarks(links);
          setSelectAll(true);
        } catch {
          setParseError("Failed to parse bookmarks file.");
        }
      };
      reader.readAsText(e.target.files[0]);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setBookmarks((bms) => bms.map((bm) => ({ ...bm, checked })));
    setSelectAll(checked);
  };

  const handleBookmarkCheck = (idx: number, checked: boolean) => {
    setBookmarks((bms) => bms.map((bm, i) => (i === idx ? { ...bm, checked } : bm)));
    if (!checked) setSelectAll(false);
    else if (bookmarks.filter((bm, i) => i !== idx && bm.checked).length === bookmarks.length - 1)
      setSelectAll(true);
  };

  const handleImport = async () => {
    setImporting(true);
    setImportSuccess("");
    setParseError("");

    const plan = planBookmarkImport(
      bookmarks.filter((bm) => bm.checked),
      dashboardItems,
      { columnCount }
    );

    try {
      // One transaction for the whole batch rather than one per bookmark.
      if (plan.items.length > 0) {
        await onImport(plan.items);
      }
      setImportSuccess(describeImportResult(plan));
    } catch (err) {
      // Previously a bare `catch {}` per item, so a failed import reported
      // "Imported 0 bookmarks!" with no indication anything had gone wrong.
      console.error("Bookmark import failed:", err);
      setParseError(
        err instanceof Error ? `Import failed: ${err.message}` : "Import failed. Please try again."
      );
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteAll = async () => {
    setConfirmDeleteAll(false);
    setDeletingAll(true);
    setDeleteNotice(null);
    try {
      await onDeleteAllLinks();
      setDeleteNotice({
        kind: "ok",
        text: "All links deleted. Your widgets and settings are untouched.",
      });
    } catch (err) {
      console.error("Delete all links failed:", err);
      setDeleteNotice({
        kind: "error",
        text:
          err instanceof Error ? `Couldn't delete links: ${err.message}` : "Couldn't delete links.",
      });
    } finally {
      setDeletingAll(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto mt-8 sm:mt-12 px-4 pb-16">
      <Link to="/" className="text-xs text-[var(--color-accent)] hover:underline mb-4 inline-block">
        &larr; Back to dashboard
      </Link>
      <h2 className="text-xl font-heading font-semibold text-[var(--color-text-primary)] mb-6">
        Settings
      </h2>

      <div className="flex flex-col gap-6">
        {/* Link behavior */}
        <section className="p-5 bg-[var(--color-bg-secondary)] rounded-[var(--radius-lg)] border border-[var(--color-border)]">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">Links</h3>
          <p className="text-xs text-[var(--color-text-tertiary)] mb-4">
            Choose what happens when you click a link on your dashboard.
          </p>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 w-4 h-4 accent-[var(--color-accent)] cursor-pointer"
              checked={openLinksInNewTab}
              onChange={(e) => setOpenLinksInNewTab(e.target.checked)}
            />
            <span className="min-w-0">
              <span className="block text-sm text-[var(--color-text-primary)]">
                Open links in a new tab
              </span>
              <span className="block text-xs text-[var(--color-text-tertiary)] mt-0.5">
                When off, links open in the current tab. Either way you can Cmd/Ctrl-click or
                middle-click to do the opposite.
              </span>
            </span>
          </label>
        </section>

        {/* Import section */}
        <section className="p-5 bg-[var(--color-bg-secondary)] rounded-[var(--radius-lg)] border border-[var(--color-border)]">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">
            Import Bookmarks
          </h3>
          <p className="text-xs text-[var(--color-text-tertiary)] mb-4">
            Upload a bookmarks HTML file exported from your browser.
          </p>
          <input
            type="file"
            accept=".html"
            onChange={handleFileChange}
            className="block w-full text-sm text-[var(--color-text-primary)]
                       file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0
                       file:text-sm file:font-medium
                       file:bg-[var(--color-bg-hover)] file:text-[var(--color-text-primary)]
                       hover:file:bg-[var(--color-border)]
                       transition-colors"
          />

          {parseError && (
            <div className="mt-3 text-sm text-[var(--color-danger)] bg-[color-mix(in_srgb,var(--color-danger)_5%,transparent)] border border-[color-mix(in_srgb,var(--color-danger)_20%,transparent)] rounded-md p-3">
              {parseError}
            </div>
          )}
          {importSuccess && (
            <div className="mt-3 text-sm text-[var(--color-success)] bg-[color-mix(in_srgb,var(--color-success)_5%,transparent)] border border-[color-mix(in_srgb,var(--color-success)_20%,transparent)] rounded-md p-3">
              {importSuccess}
            </div>
          )}

          {bookmarks.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-[var(--color-text-tertiary)]">
                  {bookmarks.filter((bm) => bm.checked).length} of {bookmarks.length} selected
                </span>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none text-xs text-[var(--color-text-primary)]">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="accent-[var(--color-accent)] w-3.5 h-3.5 rounded"
                  />
                  {selectAll ? "Unselect All" : "Select All"}
                </label>
              </div>
              <ul className="max-h-72 overflow-y-auto border border-[var(--color-border)] rounded-md bg-[var(--color-bg)] divide-y divide-[var(--color-border)]">
                {bookmarks.map((bm, idx) => (
                  <li
                    key={idx}
                    className="flex items-center gap-2.5 px-3 py-2 hover:bg-[var(--color-bg-hover)] transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={bm.checked}
                      onChange={(e) => handleBookmarkCheck(idx, e.target.checked)}
                      className="accent-[var(--color-accent)] w-3.5 h-3.5 rounded shrink-0"
                    />
                    <span
                      className="truncate text-sm text-[var(--color-text-primary)]"
                      title={bm.href}
                    >
                      {bm.title || bm.href}
                    </span>
                    {bm.folder && (
                      <span className="text-xs text-[var(--color-text-muted)] ml-auto shrink-0">
                        {bm.folder}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              <button
                className="mt-4 w-full px-4 py-2 rounded-md bg-[var(--color-accent)] text-[var(--color-text-inverse)]
                           text-sm font-medium hover:opacity-90 transition-colors
                           focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-offset-2
                           disabled:opacity-50"
                onClick={handleImport}
                disabled={importing}
              >
                {importing ? "Importing..." : "Import Selected"}
              </button>
            </div>
          )}
        </section>

        {/* Export section */}
        <section className="p-5 bg-[var(--color-bg-secondary)] rounded-[var(--radius-lg)] border border-[var(--color-border)]">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">
            Export Bookmarks
          </h3>
          <p className="text-xs text-[var(--color-text-tertiary)] mb-4">
            Download your links as a standard bookmarks file.
          </p>
          <button
            className="px-4 py-2 rounded-md border border-[var(--color-border)]
                       bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)]
                       text-sm font-medium hover:bg-[var(--color-bg-hover)] transition-colors
                       focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]
                       disabled:opacity-50"
            onClick={() => downloadBookmarksFile(dashboardItems)}
            disabled={linkCount === 0}
          >
            Export as bookmarks.html
          </button>
        </section>

        {/* Danger zone */}
        <section className="p-5 bg-[var(--color-bg-secondary)] rounded-[var(--radius-lg)] border border-[color-mix(in_srgb,var(--color-danger)_40%,transparent)]">
          <h3 className="text-sm font-semibold text-[var(--color-danger)] mb-1">Danger zone</h3>
          <p className="text-xs text-[var(--color-text-tertiary)] mb-4">
            Removes every link from your dashboard, including any that no longer render. Widgets,
            layout and settings stay. This can't be undone, so export first if you might want them
            back.
          </p>
          {deleteNotice && (
            <div
              role="status"
              className={`mb-3 text-sm rounded-md p-3 border ${
                deleteNotice.kind === "ok"
                  ? "text-[var(--color-success)] bg-[color-mix(in_srgb,var(--color-success)_5%,transparent)] border-[color-mix(in_srgb,var(--color-success)_20%,transparent)]"
                  : "text-[var(--color-danger)] bg-[color-mix(in_srgb,var(--color-danger)_5%,transparent)] border-[color-mix(in_srgb,var(--color-danger)_20%,transparent)]"
              }`}
            >
              {deleteNotice.text}
            </div>
          )}
          <button
            type="button"
            className="btn btn-danger disabled:opacity-50"
            onClick={() => setConfirmDeleteAll(true)}
            disabled={deletingAll || linkCount === 0}
          >
            {deletingAll
              ? "Deleting…"
              : `Delete all links${linkCount > 0 ? ` (${linkCount})` : ""}`}
          </button>
        </section>
      </div>

      <ConfirmDialog
        open={confirmDeleteAll}
        onClose={() => setConfirmDeleteAll(false)}
        onConfirm={handleDeleteAll}
        title="Delete all links?"
        description={`This removes ${linkCount} link${linkCount === 1 ? "" : "s"} from your dashboard and can't be undone. Widgets and settings are not affected.`}
        confirmLabel="Delete all links"
        variant="danger"
      />
    </div>
  );
};

export default Settings;
